import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionThemeValue,
} from "@store/Framework/Common/Utilities/dataOptions";
import { getGridPreset } from "@store/Framework/Common/Utilities/gridPreset";
import { LiteralUnion } from "@store/Framework/Types/Common";
import { GridPreset } from "@store/Framework/Types/Grid";
import { StyledTheme } from "@store/Framework/Types/StyledTheme";
import { useMemo } from "react";
import { useTheme } from "styled-components";

export type GapPresetValue = GridPreset;
export type GapPresetOption = DataOption<GapPresetValue | string>;
export interface GapPresetOptionMapper {
    form: GapPresetOption;
    formSmall: GapPresetOption;
}
export type GapPresetOptions = DataOptions<GapPresetOption> & GapPresetOptionMapper;
export type GapPreset = LiteralUnion<keyof GapPresetOptionMapper>;

export const gapPresetOptions: GapPresetOptions = {
    form: { id: "Form", displayName: "Form", value: "design.form.grid.base" },
    formSmall: { id: "FormSmall", displayName: "Small Form", value: "design.form.grid.small" },
};

export type GapPresetFieldValue = GapPreset | undefined;
export interface GapPresetFieldOptions {
    isChildDefinition?: boolean;
}

export const getGapPresetFieldData = (
    value: GapPresetFieldValue,
    theme: StyledTheme,
    options?: GapPresetFieldOptions,
) => {
    return value ? getDataOptionThemeValue<GapPresetValue>(gapPresetOptions, value, theme) : undefined;
};

export const getGapPresetFieldDefinition = (name: string, options?: GapPresetFieldOptions) => {
    const { isChildDefinition } = options || {};

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(gapPresetOptions),
        name,
        displayName: "Gap Preset",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        tooltip: "Do not choose a preset if you wish to customize the gap",
    };

    return definition;
};

const getGapPresetField = (name: string, options?: GapPresetFieldOptions) => {
    const parse = (value: GapPresetFieldValue, theme: StyledTheme) => getGapPresetFieldData(value, theme, options);
    const parseToGridContainer = (value: GapPresetFieldValue, theme: StyledTheme) => {
        const data = getGapPresetFieldData(value, theme);
        return data ? getGridPreset(data) : undefined;
    };

    return {
        definition: getGapPresetFieldDefinition(name, options),
        parse,
        useParse: (value: GapPresetFieldValue) => {
            const theme = useTheme();

            return useMemo(() => parse(value, theme), [value, theme]);
        },
        parseToGridContainer,
        useParseToGridContainer: (value: GapPresetFieldValue) => {
            const theme = useTheme();

            return useMemo(() => parseToGridContainer(value, theme), [value, theme]);
        },
    };
};

export default getGapPresetField;
